import { FC, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import BlockIcon from '@mui/icons-material/Block'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { useAuth } from '@/hooks/useAuth'
import { tenantService } from '@/services/tenantService'
import { TenantAdmin } from '@/types/tenant'
import { cnpjValido, mascararCnpj, normalizarCnpj } from '@/utils/cnpj'

const msgOf = (e: unknown, fallback: string): string =>
  (e as { message?: string })?.message ?? fallback

const TenantsListPage: FC = () => {
  const { usuario } = useAuth()
  const isAdmin = (usuario?.permissoes ?? []).includes('ADMIN')
  const { enqueueSnackbar } = useSnackbar()

  const [tenants, setTenants] = useState<TenantAdmin[]>([])
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editando, setEditando] = useState<TenantAdmin | null>(null)
  const [nome, setNome] = useState('')
  const [cnpj, setCnpj] = useState('')
  const [ativo, setAtivo] = useState(true)
  const [salvando, setSalvando] = useState(false)

  const carregar = useCallback(async () => {
    if (!isAdmin) return
    setLoading(true)
    setErro(null)
    try {
      setTenants(await tenantService.listar())
    } catch (e) {
      setErro(msgOf(e, 'Falha ao carregar empresas'))
    } finally {
      setLoading(false)
    }
  }, [isAdmin])

  useEffect(() => {
    carregar()
  }, [carregar])

  const abrirNovo = () => {
    setEditando(null)
    setNome('')
    setCnpj('')
    setAtivo(true)
    setDialogOpen(true)
  }

  const abrirEdicao = (t: TenantAdmin) => {
    setEditando(t)
    setNome(t.nome)
    setCnpj(t.cnpj)
    setAtivo(t.ativo)
    setDialogOpen(true)
  }

  const salvar = async () => {
    setSalvando(true)
    try {
      const payload = { nome: nome.trim(), cnpj: normalizarCnpj(cnpj), ativo }
      if (editando) {
        await tenantService.atualizar(editando.id, payload)
        enqueueSnackbar('Empresa atualizada', { variant: 'success' })
      } else {
        await tenantService.criar(payload)
        enqueueSnackbar('Empresa criada', { variant: 'success' })
      }
      setDialogOpen(false)
      await carregar()
    } catch (e) {
      enqueueSnackbar(msgOf(e, 'Falha ao salvar empresa'), { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  const desativar = async (t: TenantAdmin) => {
    if (!window.confirm(`Desativar a empresa "${t.nome}"?`)) return
    try {
      await tenantService.desativar(t.id)
      enqueueSnackbar('Empresa desativada', { variant: 'success' })
      await carregar()
    } catch (e) {
      enqueueSnackbar(msgOf(e, 'Falha ao desativar'), { variant: 'error' })
    }
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Acesso restrito a administradores.</Alert>
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title="Empresas"
        subtitle="Empresas às quais os consultores estão vinculados. Cada consultor só vê os dados da própria empresa."
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo}>
            Nova empresa
          </Button>
        }
      />

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>CNPJ</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>{t.nome}</TableCell>
                    <TableCell>{t.cnpj}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t.ativo ? 'Ativo' : 'Inativo'}
                        color={t.ativo ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => abrirEdicao(t)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {t.ativo && (
                        <Tooltip title="Desativar">
                          <IconButton size="small" color="error" onClick={() => desativar(t)}>
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {tenants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        Nenhuma empresa cadastrada.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? 'Editar empresa' : 'Nova empresa'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nome da empresa"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              fullWidth
              autoFocus
            />
            <TextField
              label="CNPJ"
              value={cnpj}
              onChange={(e) => setCnpj(mascararCnpj(e.target.value))}
              fullWidth
              placeholder="00.000.000/0000-00"
              error={cnpj.length > 0 && normalizarCnpj(cnpj).length === 14 && !cnpjValido(cnpj)}
              helperText={
                cnpj.length > 0 && normalizarCnpj(cnpj).length === 14 && !cnpjValido(cnpj)
                  ? 'CNPJ inválido: confira os dígitos verificadores'
                  : 'Numérico ou alfanumérico (letras e números + 2 dígitos verificadores)'
              }
            />
            <FormControlLabel
              control={<Switch checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />}
              label="Ativo"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={salvar}
            disabled={salvando || !nome.trim() || !cnpjValido(cnpj)}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TenantsListPage
